import { setHours, setMinutes } from 'date-fns';

import { createForm } from '@components/form';
import { FormLabel } from '@components/form-label';
import { FormStoreInstance } from '@components/form/create-form-store';
import { IconEmail } from '@components/icons/email';
import { IconSms } from '@components/icons/phone-sms';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { createMinuteOptions, getHour, getMinute } from '@utils/hoursOptions';

import { JourneyActionDetailed, TravellerJourneyActionDetailed } from 'api/types';
import { FormData } from 'ui/actions/combo-send-form/traveller-sidebar';
import { ActionTimeSelector } from 'ui/actions/common';
import { useFeature } from 'ui/hooks';

import {
  REMINDER_SUMMARY_TEMPLATES,
  buildReminderSummaryParts,
  getTimezoneName,
} from './reminder-summary';

const Form = createForm<FormData>();

export const EditRemindersContent = ({
  action,
  formStore,
  isFirstReminderCompleted,
}: {
  action: TravellerJourneyActionDetailed | JourneyActionDetailed;
  formStore: FormStoreInstance;
  isFirstReminderCompleted?: boolean;
}) => {
  const { t } = useScopedTranslate(remindersLocale);
  const { isEnabled: hasSMSEnabled } = useFeature('FEATURE_SMS');

  const reminder = action.reminder;

  const values = formStore.useFormValues();
  const isEmailNotifyChecked = values['emailNotify'] as boolean;
  const isSmsNotifyChecked = values['smsNotify'] as boolean;

  const hasEmailReminder = values['emailReminderSwitch'] as boolean;
  const hasSmsReminder = values['smsReminderSwitch'] as boolean;

  const minuteOptions = createMinuteOptions(0, 59, 15);
  const defaultHour = 9;
  const defaultMinute = 0;

  const hour = values['hour'] as string;
  const minute = values['minute'] as string;

  const initialDate = setHours(setMinutes(new Date(), defaultMinute), defaultHour);

  return (
    <Stack space={4}>
      <Text font="regular14" color="textSecondary" text={t('description')} />
      <Stack space={4}>
        <Stack space={2}>
          <FormLabel text={t('reminderMethod')} />
          <RemindersMethod
            method="email"
            defaultValue={reminder?.emailNotify ?? false}
            isDisabled={!isEmailNotifyChecked}
            onChange={(value) => {
              formStore.actions.setValue('emailReminderSwitch', value);
            }}
          />
          {hasSMSEnabled && (
            <RemindersMethod
              method="sms"
              defaultValue={reminder?.smsNotify ?? false}
              isDisabled={!isSmsNotifyChecked}
              onChange={(value) => {
                formStore.actions.setValue('smsReminderSwitch', value);
              }}
            />
          )}
        </Stack>
        {(hasEmailReminder || hasSmsReminder) && (
          <Stack space={4}>
            <Stack space={2}>
              <Stack orientation="horizontal" space={1}>
                <Text font="semibold14" text={t('amount')} />
                <Text font="regular14" text={t('timesInParentheses')} color="textHelper" />
              </Stack>
              <Form.NumberField
                id="amount"
                label={t('amount')}
                validators={['required']}
                hideLabel
                defaultValue={reminder?.amount ? Number(reminder.amount) : 1}
                min={1}
                hasSteppers
                isDisabled={isFirstReminderCompleted}
              />
            </Stack>
            <Stack space={2}>
              <Stack orientation="horizontal" space={1}>
                <Text font="semibold14" text={t('frequency')} />
                <Text font="regular14" text={t('daysInParentheses')} color="textHelper" />
              </Stack>
              <Form.NumberField
                id="frequency"
                label={t('frequency')}
                validators={['required']}
                hideLabel
                defaultValue={reminder?.frequency ? Number(reminder.frequency) : 3}
                min={1}
                hasSteppers
                isDisabled={isFirstReminderCompleted}
              />
            </Stack>
            <Stack space={2}>
              <Stack orientation="horizontal" space={1}>
                <Text font="semibold14" text={t('reminderDays')} />
                <Text font="regular14" text={t('sendOnInParentheses')} color="textHelper" />
              </Stack>
              <Form.WeekdayPicker
                id="allowedWeekdays"
                defaultValue={reminder?.allowedWeekdays ?? DEFAULT_WEEKDAYS}
                minSelectedTooltip={t('minOneDayRequired')}
              />
            </Stack>
            <ActionTimeSelector
              defaultHour={reminder?.hours ?? initialDate.getHours().toString()}
              defaultMinute={reminder?.minutes ?? initialDate.getMinutes().toString()}
              customMinuteOptions={minuteOptions}
              isRequired={false}
              isDisabled={isFirstReminderCompleted}
            />
            <Text
              font="regular14"
              color="textSecondary"
              text={buildRepeatSummary({
                amount: values['amount'] as number | undefined,
                frequency: values['frequency'] as number | undefined,
                hour: getHour({ hour, defaultHour }),
                minute: getMinute({ minute, defaultMinute }),
                timezoneName: getTimezoneName(action.timezone),
                allowedWeekdays:
                  (values['allowedWeekdays'] as number[] | undefined) ??
                  reminder?.allowedWeekdays ??
                  DEFAULT_WEEKDAYS,
                hasEmailReminder,
                hasSmsReminder,
              })}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

const RemindersMethod = ({
  method,
  defaultValue,
  isDisabled,
  onChange,
}: {
  method: 'email' | 'sms';
  defaultValue: boolean;
  isDisabled: boolean;
  onChange?: (value: boolean) => void;
}) => {
  const { t } = useScopedTranslate(remindersLocale);

  const icon =
    method === 'email' ? (
      <IconEmail color="elementPurple50" />
    ) : (
      <IconSms color="elementPurple50" />
    );
  const text = method === 'email' ? t('email') : t('sms');
  const method_id = method === 'email' ? 'emailReminderSwitch' : 'smsReminderSwitch';

  return (
    <Stack space={4} orientation="horizontal" align="center" justify="space-between">
      <Stack orientation="horizontal" space={1} align="center">
        {icon}
        <Text font="semibold14" text={text} color={isDisabled ? 'textHelper' : 'textPrimary'} />
      </Stack>
      <Form.Switch
        id={method_id}
        label={text}
        defaultValue={defaultValue ?? false}
        labelVisible={false}
        isDisabled={isDisabled}
        onChange={onChange}
      />
    </Stack>
  );
};

const DEFAULT_WEEKDAYS = [1, 2, 3, 4, 5];

type RepeatSummaryArgs = {
  amount: number | undefined;
  frequency: number | undefined;
  hour: string;
  minute: string;
  timezoneName: string;
  allowedWeekdays: number[];
  hasEmailReminder: boolean;
  hasSmsReminder: boolean;
};

const HIGHLIGHT = { font: 'semibold14', color: 'elementBlue100' } as const;

const buildRepeatSummary = ({
  amount,
  frequency,
  hour,
  minute,
  timezoneName,
  allowedWeekdays,
  hasEmailReminder,
  hasSmsReminder,
}: RepeatSummaryArgs): JSX.Element | null => {
  const amountValue = amount ?? 0;
  const frequencyValue = frequency ?? 0;
  if (amountValue <= 0 || frequencyValue <= 0) return null;
  if (!hasEmailReminder && !hasSmsReminder) return null;

  const { styledTranslate } = getScopedTranslate(remindersLocale);
  const parts = buildReminderSummaryParts({
    amount: amountValue,
    frequency: frequencyValue,
    hour,
    minute,
    allowedWeekdays,
    hasEmail: hasEmailReminder,
    hasSms: hasSmsReminder,
    timezoneName,
  });

  if (frequencyValue === 1) {
    return styledTranslate('repeatSummaryEveryDay', {
      ...parts,
      amount: { ...HIGHLIGHT, text: parts.amount },
      time: { ...HIGHLIGHT, text: parts.time },
    });
  }

  return styledTranslate('repeatSummaryEveryNDays', {
    ...parts,
    amount: { ...HIGHLIGHT, text: parts.amount },
    time: { ...HIGHLIGHT, text: parts.time },
    frequency: { ...HIGHLIGHT, text: parts.frequency },
  });
};

const remindersLocale = {
  en: {
    description: 'Send automatic reminders when form has not been completed',
    reminderMethod: 'Reminder method',
    email: 'Email',
    sms: 'SMS',
    hour: 'Hour',
    minutes: 'Minute',
    frequency: 'Reminder frequency',
    daysInParentheses: '(Days)',
    amount: 'Max reminders',
    timesInParentheses: '(Times)',
    reminderDays: 'Reminder days',
    sendOnInParentheses: '(Send on)',
    minOneDayRequired: 'You must keep at least one day selected',
    repeatSummaryEveryDay: REMINDER_SUMMARY_TEMPLATES.everyDay,
    repeatSummaryEveryNDays: REMINDER_SUMMARY_TEMPLATES.everyNDays,
  },
};
